package com.milta.feature.admin.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.admin.domain.entity.AdminClaudeUsagePeriod
import com.milta.feature.admin.domain.usecase.GetAdminClaudeUsageEventsUseCase
import com.milta.feature.admin.domain.usecase.GetAdminClaudeUsageUseCase
import kotlinx.coroutines.launch

/** ViewModel for the Admin "Conso. IA" screen (real per-account/per-organisation Claude token
 * consumption — distinct from AdminOcrAnalysisViewModel's background cost-comparison experiment). */
class AdminClaudeUsageViewModel(
    private val getClaudeUsage: GetAdminClaudeUsageUseCase,
    private val getClaudeUsageEvents: GetAdminClaudeUsageEventsUseCase,
) : MviViewModel<AdminClaudeUsageState, AdminClaudeUsageIntent>(AdminClaudeUsageState()) {

    private companion object {
        const val EventsPageSize = 20
    }

    init {
        onIntent(AdminClaudeUsageIntent.Load)
    }

    override fun onIntent(intent: AdminClaudeUsageIntent) {
        when (intent) {
            AdminClaudeUsageIntent.Load -> load()
            is AdminClaudeUsageIntent.ChangePeriod -> changePeriod(intent.period)
            is AdminClaudeUsageIntent.ChangeView -> setState { copy(view = intent.view) }
            is AdminClaudeUsageIntent.ToggleAccountRow -> toggleAccountRow(intent.accountId)
        }
    }

    private fun load() {
        scope.launch {
            setState { copy(isLoading = true, error = null) }
            getClaudeUsage(currentState.period).fold(
                onSuccess = { usage -> setState { copy(isLoading = false, usage = usage) } },
                onFailure = { e ->
                    val errorCode = if (e is ApiException) "server_error" else "offline"
                    setState { copy(isLoading = false, error = errorCode) }
                },
            )
        }
    }

    // Changing the period invalidates any cached per-call detail (it was fetched for the previous
    // window) — collapse and clear rather than show stale events under the new period.
    private fun changePeriod(period: AdminClaudeUsagePeriod) {
        setState { copy(period = period, expandedAccountId = null, eventsByAccount = emptyMap()) }
        load()
    }

    private fun toggleAccountRow(accountId: String) {
        if (currentState.expandedAccountId == accountId) {
            setState { copy(expandedAccountId = null) }
            return
        }

        setState { copy(expandedAccountId = accountId) }

        if (currentState.eventsByAccount.containsKey(accountId))
            return

        scope.launch {
            setState { copy(eventsLoadingAccountId = accountId) }
            getClaudeUsageEvents(accountId, currentState.period, 1, EventsPageSize).fold(
                onSuccess = { events ->
                    setState {
                        copy(eventsLoadingAccountId = null, eventsByAccount = eventsByAccount + (accountId to events))
                    }
                },
                onFailure = { setState { copy(eventsLoadingAccountId = null) } },
            )
        }
    }
}
